import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

type SimulationStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

/**
 * Format duration in seconds to human-readable format
 */
const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) {
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
};

/**
 * Format currency with proper formatting
 */
const formatCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '--';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Format percentage with proper formatting
 */
const formatPercent = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '--';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '--';
  const sign = num >= 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
};

/**
 * Get status badge color classes
 */
const getStatusBadgeClasses = (status: SimulationStatus): string => {
  const baseClasses = 'px-2.5 py-1 text-xs font-medium rounded-full';
  switch (status) {
    case 'COMPLETED':
      return `${baseClasses} bg-green-500/20 text-green-400 border border-green-500/30`;
    case 'RUNNING':
      return `${baseClasses} bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 animate-pulse`;
    case 'PENDING':
      return `${baseClasses} bg-yellow-500/20 text-yellow-400 border border-yellow-500/30`;
    case 'FAILED':
      return `${baseClasses} bg-red-500/20 text-red-400 border border-red-500/30`;
    case 'CANCELLED':
      return `${baseClasses} bg-neutral-500/20 text-neutral-400 border border-neutral-500/30`;
    default:
      return `${baseClasses} bg-neutral-500/20 text-neutral-400 border border-neutral-500/30`;
  }
};

interface SimulationUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface SimulationUserDefaults {
  userId: string;
  accountId: string;
  environment: 'PAPER' | 'LIVE' | string;
  cashUsd: number | null;
  buyingPowerUsd: number | null;
  equityUsd: number | null;
  initialCapitalUsd: number;
  riskTolerancePreset: string | null;
}

interface SimulationRun {
  id: string;
  status: SimulationStatus;
  startDate: string;
  endDate: string;
  timezone: string;
  initialCapital: string | number;
  finalCapital?: string | number | null;
  metrics?: any;
  recommendations?: any;
  error?: string | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  targetUser?: { email: string; firstName: string; lastName: string };
  requestedBy?: { email: string };
}

interface EquityPoint {
  id: string;
  timestamp: string;
  equity: string | number;
}

interface TradeRow {
  id: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: string | number;
  price: string | number;
  timestamp: string;
  pnl?: string | number | null;
  pnlPercent?: string | number | null;
  reason?: string | null;
}

interface ProgressData {
  progress: number;
  currentSession: number;
  totalSessions: number;
  estimatedRemainingSeconds: number | null;
  estimateReliable: boolean;
  elapsedSeconds: number | null;
  avgSecondsPerSession: number | null;
  currentDate: string | null;
  currentSessionTime: string | null;
}

/**
 * Collapsible Section Component
 */
const CollapsibleSection: React.FC<{
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, defaultOpen = true, badge, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-neutral-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-neutral-800/50 hover:bg-neutral-800 transition-colors"
        type="button"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <svg
            className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="text-left">
            <span className="text-white font-medium">{title}</span>
            {subtitle && <span className="text-neutral-500 text-sm ml-2">{subtitle}</span>}
          </div>
        </div>
        {badge}
      </button>
      {isOpen && <div className="p-4 bg-neutral-900/50">{children}</div>}
    </div>
  );
};

/**
 * Metric Card Component
 */
const MetricCard: React.FC<{
  label: string;
  value: string;
  subValue?: string;
  trend?: 'up' | 'down' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
}> = ({ label, value, subValue, trend, size = 'md' }) => {
  const trendColor = trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-neutral-200';
  const sizeClasses = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
  };
  const valueSizeClasses = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  return (
    <div className={`bg-neutral-800/50 border border-neutral-700 rounded-lg ${sizeClasses[size]}`}>
      <div className="text-neutral-400 text-xs uppercase tracking-wide mb-1">{label}</div>
      <div className={`${valueSizeClasses[size]} font-semibold ${trendColor}`}>{value}</div>
      {subValue && <div className="text-neutral-500 text-xs mt-1">{subValue}</div>}
    </div>
  );
};

/**
 * Progress Ring Component
 */
const ProgressRing: React.FC<{ progress: number; size?: number; strokeWidth?: number }> = ({
  progress,
  size = 48,
  strokeWidth = 4,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-neutral-700"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-cyan-500 transition-all duration-300"
      />
    </svg>
  );
};

/**
 * Simulation Dashboard Component
 */
const SimulationDashboard: React.FC = () => {
  // State for users and form
  const [users, setUsers] = useState<SimulationUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUserDefaults, setSelectedUserDefaults] = useState<SimulationUserDefaults | null>(null);
  const [startDate, setStartDate] = useState<string>('2025-08-01');
  const [endDate, setEndDate] = useState<string>('2025-10-31');

  // State for runs
  const [runs, setRuns] = useState<SimulationRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>('');
  const [selectedRun, setSelectedRun] = useState<SimulationRun | null>(null);
  const [equity, setEquity] = useState<EquityPoint[]>([]);
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);

  // State for UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tradesPage, setTradesPage] = useState(0);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [showStats, setShowStats] = useState(true);

  const TRADES_PER_PAGE = 15;

  // Fetch functions
  const fetchUsers = useCallback(async () => {
    const res = await api.get('/admin/simulations/users');
    setUsers(res.data.data || []);
  }, []);

  const fetchUserDefaults = useCallback(async (userId: string) => {
    const res = await api.get(`/admin/simulations/users/${userId}/defaults`);
    setSelectedUserDefaults(res.data.data || null);
  }, []);

  const fetchRuns = useCallback(async () => {
    const res = await api.get('/admin/simulations');
    const runsData = res.data.data || [];
    // Sort by createdAt descending
    runsData.sort((a: SimulationRun, b: SimulationRun) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    setRuns(runsData);
  }, []);

  const fetchRunDetails = useCallback(async (runId: string) => {
    const [runRes, equityRes, tradesRes] = await Promise.all([
      api.get(`/admin/simulations/${runId}`),
      api.get(`/admin/simulations/${runId}/equity`),
      api.get(`/admin/simulations/${runId}/trades`),
    ]);
    setSelectedRun(runRes.data.data || null);
    setEquity(equityRes.data.data || []);
    setTrades(tradesRes.data.data || []);
    setTradesPage(0);
  }, []);

  const fetchProgress = useCallback(async (runId: string) => {
    try {
      const res = await api.get(`/admin/simulations/${runId}/progress`);
      const progress = res.data.data;
      setProgressData({
        progress: progress.progress || 0,
        currentSession: progress.currentSession || 0,
        totalSessions: progress.totalSessions || 0,
        estimatedRemainingSeconds: progress.estimatedRemainingSeconds ?? null,
        estimateReliable: progress.estimateReliable ?? false,
        elapsedSeconds: progress.elapsedSeconds ?? null,
        avgSecondsPerSession: progress.avgSecondsPerSession ?? null,
        currentDate: progress.currentDate || null,
        currentSessionTime: progress.currentSessionTime || null,
      });
      return progress.status === 'RUNNING';
    } catch (e) {
      return false;
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setError(null);
        await Promise.all([fetchUsers(), fetchRuns()]);
      } catch (e: any) {
        setError(e?.message || 'Failed to load simulation dashboard');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchUsers, fetchRuns]);

  // Refresh runs list periodically if there are running simulations
  useEffect(() => {
    const hasRunningSimulations = runs.some((r) => r.status === 'RUNNING');
    if (!hasRunningSimulations) return;

    const interval = setInterval(async () => {
      try {
        await fetchRuns();
      } catch (e) {
        // Silently fail - don't show errors for background refresh
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [runs, fetchRuns]);

  // Poll for progress when a run is selected and running
  useEffect(() => {
    if (!selectedRunId || !selectedRun || selectedRun.status !== 'RUNNING') {
      setProgressData(null);
      return;
    }

    const pollProgress = async () => {
      const isRunning = await fetchProgress(selectedRunId);
      if (!isRunning) {
        await fetchRunDetails(selectedRunId);
        await fetchRuns();
      } else {
        try {
          const tradesRes = await api.get(`/admin/simulations/${selectedRunId}/trades`);
          setTrades(tradesRes.data.data || []);
        } catch (e) {
          // Silently fail
        }
      }
    };

    pollProgress();
    const interval = setInterval(pollProgress, 3000);

    return () => clearInterval(interval);
  }, [selectedRunId, selectedRun, fetchProgress, fetchRunDetails, fetchRuns]);

  // Fetch user defaults when user is selected
  useEffect(() => {
    const fetchDefaults = async () => {
      if (!selectedUserId) {
        setSelectedUserDefaults(null);
        return;
      }

      try {
        setError(null);
        await fetchUserDefaults(selectedUserId);
      } catch (e: any) {
        setSelectedUserDefaults(null);
        setError(e?.response?.data?.error || e?.message || 'Failed to load user defaults');
      }
    };
    fetchDefaults();
  }, [selectedUserId, fetchUserDefaults]);

  // Computed values
  const startIso = useMemo(() => `${startDate}T00:00:00.000Z`, [startDate]);
  const endIso = useMemo(() => `${endDate}T00:00:00.000Z`, [endDate]);

  const equityChartData = useMemo(() => {
    const data = equity.map((p) => ({
      timestamp: new Date(p.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      equity: Number(p.equity),
      date: new Date(p.timestamp),
    }));
    return data;
  }, [equity]);

  const initialEquity = useMemo(() => {
    if (equityChartData.length === 0) return null;
    return equityChartData[0]?.equity ?? null;
  }, [equityChartData]);

  const sortedTrades = useMemo(() => {
    const sorted = [...trades];
    sorted.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  }, [trades, sortOrder]);

  const paginatedTrades = useMemo(() => {
    const start = tradesPage * TRADES_PER_PAGE;
    return sortedTrades.slice(start, start + TRADES_PER_PAGE);
  }, [sortedTrades, tradesPage]);

  const totalTradesPages = Math.ceil(trades.length / TRADES_PER_PAGE);

  // Calculate metrics for selected run
  const runMetrics = useMemo(() => {
    if (!selectedRun) return null;

    const initial = Number(selectedRun.initialCapital);
    const final = selectedRun.finalCapital ? Number(selectedRun.finalCapital) : null;
    const returnPct = final ? ((final - initial) / initial) * 100 : null;
    const pnl = final ? final - initial : null;

    const winningTrades = trades.filter((t) => Number(t.pnl || 0) > 0).length;
    const losingTrades = trades.filter((t) => Number(t.pnl || 0) < 0).length;
    const winRate = trades.length > 0 ? (winningTrades / trades.length) * 100 : null;

    const totalPnl = trades.reduce((sum, t) => sum + Number(t.pnl || 0), 0);
    const avgPnl = trades.length > 0 ? totalPnl / trades.length : null;

    return {
      initial,
      final,
      returnPct,
      pnl,
      totalTrades: trades.length,
      winningTrades,
      losingTrades,
      winRate,
      avgPnl,
    };
  }, [selectedRun, trades]);

  // Chart data for Win/Loss Distribution
  const winLossChartData = useMemo(() => {
    if (!runMetrics) return [];
    return [
      { name: 'Wins', value: runMetrics.winningTrades, color: '#22c55e' },
      { name: 'Losses', value: runMetrics.losingTrades, color: '#ef4444' },
    ];
  }, [runMetrics]);

  // Chart data for Trade Outcomes
  const tradeOutcomesData = useMemo(() => {
    if (trades.length === 0) return [];
    
    const bigWins = trades.filter((t) => {
      const pnl = Number(t.pnl || 0);
      const pct = Number(t.pnlPercent || 0);
      return pnl > 0 && pct >= 5;
    }).length;

    const smallWins = trades.filter((t) => {
      const pnl = Number(t.pnl || 0);
      const pct = Number(t.pnlPercent || 0);
      return pnl > 0 && pct < 5;
    }).length;

    const smallLosses = trades.filter((t) => {
      const pnl = Number(t.pnl || 0);
      const pct = Number(t.pnlPercent || 0);
      return pnl < 0 && pct > -5;
    }).length;

    const bigLosses = trades.filter((t) => {
      const pnl = Number(t.pnl || 0);
      const pct = Number(t.pnlPercent || 0);
      return pnl < 0 && pct <= -5;
    }).length;

    return [
      { name: 'Big Win (+5%)', value: bigWins, color: '#16a34a' },
      { name: 'Win (0-5%)', value: smallWins, color: '#22c55e' },
      { name: 'Loss (0-5%)', value: smallLosses, color: '#f87171' },
      { name: 'Big Loss (-5%)', value: bigLosses, color: '#dc2626' },
    ].filter((item) => item.value > 0);
  }, [trades]);

  // Chart data for Monthly/Weekly P&L
  const monthlyPlData = useMemo(() => {
    if (trades.length === 0) return [];

    // Group trades by month
    const monthlyMap = new Map<string, number>();
    trades.forEach((t) => {
      const date = new Date(t.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const pnl = Number(t.pnl || 0);
      monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + pnl);
    });

    // Convert to array and sort by date
    return Array.from(monthlyMap.entries())
      .map(([monthKey, pnl]) => {
        const [year = '0', month = '0'] = monthKey.split('-');
        const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1);
        return {
          month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          pnl,
          color: pnl >= 0 ? '#22c55e' : '#ef4444',
          sortDate: date.getTime(),
        };
      })
      .sort((a, b) => a.sortDate - b.sortDate);
  }, [trades]);

  // Handlers
  const handleCreateRun = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.post('/admin/simulations', {
        targetUserId: selectedUserId,
        startDate: startIso,
        endDate: endIso,
        timezone: 'America/New_York',
      });
      const runId = res.data?.data?.runId;
      await fetchRuns();
      if (runId) {
        setSelectedRunId(runId);
        await fetchRunDetails(runId);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to create simulation run');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRun = async (run: SimulationRun) => {
    if (!confirm(`Delete simulation run for ${run.targetUser?.email}?`)) return;
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/admin/simulations/${run.id}`);
      if (selectedRunId === run.id) {
        setSelectedRunId('');
        setSelectedRun(null);
        setEquity([]);
        setTrades([]);
      }
      await fetchRuns();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRun = async (runId: string) => {
    setSelectedRunId(runId);
    await fetchRunDetails(runId);
  };

  // Render recommendations content
  const renderRecommendations = () => {
    if (!selectedRun?.recommendations) {
      return <p className="text-neutral-500 text-sm italic">No recommendations generated yet.</p>;
    }

    const recs = selectedRun.recommendations;
    const hasError = !!recs.error;

    return (
      <div className="space-y-4">
        {/* Provider info and error display */}
        {(recs.provider || recs.error) && (
          <div className={`p-3 rounded-lg border ${hasError ? 'bg-red-900/20 border-red-500/30' : 'bg-neutral-800/30 border-neutral-700'}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs text-neutral-400">
                {recs.provider && (
                  <span>
                    Provider: <span className="text-neutral-200">{recs.provider === 'openai' ? 'OpenAI' : recs.provider}</span>
                  </span>
                )}
                {recs.model && (
                  <span className="ml-2">
                    Model: <span className="text-neutral-200">{recs.model}</span>
                  </span>
                )}
              </div>
            </div>
            {hasError && (
              <div className="mt-2 p-2 bg-red-900/30 rounded border border-red-500/20">
                <div className="text-red-400 text-xs font-medium mb-1">Recommendation Generation Failed</div>
                <div className="text-red-300 text-sm break-words">{recs.error}</div>
                {recs.error?.includes('API key') && (
                  <div className="mt-2 text-xs text-yellow-400">
                    Tip: Check if your OpenAI API key is valid at https://platform.openai.com/account/api-keys
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        {recs.summary && (
          <div>
            <div className="text-sm font-medium text-neutral-300 mb-2">Summary</div>
            <p className="text-neutral-200 text-sm leading-relaxed bg-neutral-800/30 p-3 rounded-lg border border-neutral-700">
              {recs.summary}
            </p>
          </div>
        )}

        {/* Key findings */}
        {(recs.key_findings || []).length > 0 && (
          <div>
            <div className="text-sm font-medium text-neutral-300 mb-2">Key Findings</div>
            <ul className="space-y-2">
              {(recs.key_findings as string[]).map((item: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-neutral-200">
                  <span className="text-cyan-400 mt-1">*</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Main recommendations */}
        {(recs.items || recs.recommendations || []).length > 0 && (
          <div>
            <div className="text-sm font-medium text-neutral-300 mb-2">Recommendations</div>
            <ul className="space-y-2">
              {((recs.items || recs.recommendations) as string[]).map((item: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-neutral-200">
                  <span className="text-green-400 mt-1">*</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Policy tuning ideas */}
        {(recs.policy_tuning_ideas || []).length > 0 && (
          <CollapsibleSection
            title="Policy Tuning Ideas"
            subtitle="Requires manual review"
            defaultOpen={false}
            badge={
              <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">
                {recs.policy_tuning_ideas.length} ideas
              </span>
            }
          >
            <ul className="space-y-2">
              {(recs.policy_tuning_ideas as string[]).map((item: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-neutral-200">
                  <span className="text-yellow-400 mt-1">*</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {/* App improvements */}
        {(recs.app_improvements || []).length > 0 && (
          <CollapsibleSection
            title="App Improvements"
            subtitle="Engineering suggestions"
            defaultOpen={false}
            badge={
              <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-full">
                {recs.app_improvements.length} suggestions
              </span>
            }
          >
            <ul className="space-y-2">
              {(recs.app_improvements as string[]).map((item: string, idx: number) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-neutral-200">
                  <span className="text-purple-400 mt-1">*</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {/* Caveats */}
        {(recs.caveats || []).length > 0 && (
          <div className="mt-4 p-3 bg-amber-900/10 border border-amber-500/20 rounded-lg">
            <div className="text-xs font-medium text-amber-400 mb-2">Caveats</div>
            <ul className="space-y-1">
              {(recs.caveats as string[]).map((item: string, idx: number) => (
                <li key={idx} className="text-xs text-amber-300/80 flex items-start gap-2">
                  <span className="text-amber-500">!</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Simulation</h2>
          <p className="text-neutral-400 text-sm mt-1">
            Run the production trading pipeline against historical data. Output is read-only and stored in simulation tables.
          </p>
        </div>
        <button
          onClick={fetchRuns}
          className="btn btn-outline px-4 py-2 text-sm flex items-center gap-2"
          disabled={loading}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <div className="text-red-400 font-medium">Error</div>
            <p className="text-red-300 text-sm mt-1">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Create Simulation Form */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Simulation Run
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* User Selection */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-neutral-300 mb-2">User</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
            >
              <option value="">Select user...</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} ({u.email})
                </option>
              ))}
            </select>
            {selectedUserDefaults && (
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="px-2 py-1 text-xs bg-neutral-800 border border-neutral-700 rounded-full text-neutral-300">
                  {selectedUserDefaults.environment}
                </span>
                <span className="px-2 py-1 text-xs bg-neutral-800 border border-neutral-700 rounded-full text-neutral-300">
                  Risk: {selectedUserDefaults.riskTolerancePreset || 'MEDIUM'}
                </span>
              </div>
            )}
          </div>

          {/* Initial Capital Display */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-neutral-300 mb-2">Initial Capital</label>
            <div className="bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white">
              {selectedUserDefaults ? formatCurrency(selectedUserDefaults.initialCapitalUsd) : '--'}
            </div>
            {selectedUserDefaults && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                <div className="px-2 py-1.5 bg-neutral-800/50 border border-neutral-700/50 rounded text-center">
                  <div className="text-[10px] text-neutral-500 uppercase">Cash</div>
                  <div className="text-xs text-neutral-200">{formatCurrency(selectedUserDefaults.cashUsd)}</div>
                </div>
                <div className="px-2 py-1.5 bg-neutral-800/50 border border-neutral-700/50 rounded text-center">
                  <div className="text-[10px] text-neutral-500 uppercase">Buying Power</div>
                  <div className="text-xs text-neutral-200">{formatCurrency(selectedUserDefaults.buyingPowerUsd)}</div>
                </div>
                <div className="px-2 py-1.5 bg-neutral-800/50 border border-neutral-700/50 rounded text-center">
                  <div className="text-[10px] text-neutral-500 uppercase">Equity</div>
                  <div className="text-xs text-neutral-200">{formatCurrency(selectedUserDefaults.equityUsd)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-300 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Run Button */}
          <div className="lg:col-span-2 flex items-end">
            <button
              onClick={handleCreateRun}
              disabled={!selectedUserId || !selectedUserDefaults || loading}
              className={`w-full px-6 py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                !selectedUserId || !selectedUserDefaults || loading
                  ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed'
                  : 'bg-cyan-600 hover:bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Running...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Run Simulation
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Recent Runs List */}
        <div className="xl:col-span-2 bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-800 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Recent Runs</h3>
            <span className="text-sm text-neutral-400">{runs.length} total</span>
          </div>

          <div className="divide-y divide-neutral-800 max-h-[600px] overflow-y-auto">
            {runs.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-12 h-12 text-neutral-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-neutral-400">No simulation runs yet</p>
                <p className="text-neutral-500 text-sm mt-1">Create a new run to get started</p>
              </div>
            ) : (
              runs.map((run) => {
                const isSelected = selectedRunId === run.id;
                const returnPct = run.metrics?.totalReturnPercent;

                return (
                  <div
                    key={run.id}
                    className={`p-4 cursor-pointer transition-colors ${
                      isSelected ? 'bg-cyan-900/20 border-l-2 border-cyan-500' : 'hover:bg-neutral-800/50 border-l-2 border-transparent'
                    }`}
                    onClick={() => handleViewRun(run.id)}
                    onKeyDown={(e) => e.key === 'Enter' && handleViewRun(run.id)}
                    tabIndex={0}
                    role="button"
                    aria-pressed={isSelected}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium truncate">{run.targetUser?.email || 'Unknown'}</span>
                          <span className={getStatusBadgeClasses(run.status)}>{run.status}</span>
                        </div>
                        <div className="text-xs text-neutral-400 mb-2">
                          {new Date(run.startDate).toLocaleDateString()} - {new Date(run.endDate).toLocaleDateString()}
                        </div>

                        {/* Progress bar for running simulations */}
                        {run.status === 'RUNNING' && run.metrics && typeof run.metrics.progress === 'number' && (
                          <div className="mt-2">
                            <div className="h-1.5 bg-neutral-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-300 ${
                                  run.metrics.estimateReliable === false ? 'bg-yellow-500' : 'bg-cyan-500'
                                }`}
                                style={{ width: `${run.metrics.progress}%` }}
                              />
                            </div>
                            <div className="flex items-center justify-between text-xs text-neutral-500 mt-1">
                              <span>{run.metrics.progress}%</span>
                              {run.metrics.estimatedRemainingSeconds > 0 && (
                                <span className={run.metrics.estimateReliable === false ? 'text-yellow-400' : ''}>
                                  ~{formatDuration(run.metrics.estimatedRemainingSeconds)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        {typeof returnPct === 'number' && (
                          <span className={`text-lg font-semibold ${returnPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatPercent(returnPct)}
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRun(run);
                          }}
                          className="p-1.5 text-neutral-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors"
                          title="Delete run"
                          disabled={loading}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Run Details */}
        <div className="xl:col-span-3 space-y-6">
          {!selectedRun ? (
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-12 text-center">
              <div className="max-w-md mx-auto">
                <svg className="w-20 h-20 text-neutral-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-xl font-semibold text-white mb-2">Select a Simulation Run</h3>
                <p className="text-neutral-400 mb-6">Click on a run from the list to view its details, charts, and recommendations.</p>
                
                {/* Quick stats about all runs */}
                {runs.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-6">
                    <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-3">
                      <div className="text-2xl font-bold text-cyan-400">
                        {runs.filter(r => r.status === 'COMPLETED').length}
                      </div>
                      <div className="text-xs text-neutral-400 mt-1">Completed</div>
                    </div>
                    <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-3">
                      <div className="text-2xl font-bold text-yellow-400">
                        {runs.filter(r => r.status === 'RUNNING').length}
                      </div>
                      <div className="text-xs text-neutral-400 mt-1">Running</div>
                    </div>
                    <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-3">
                      <div className="text-2xl font-bold text-red-400">
                        {runs.filter(r => r.status === 'FAILED').length}
                      </div>
                      <div className="text-xs text-neutral-400 mt-1">Failed</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Progress Card (for running simulations) */}
              {selectedRun.status === 'RUNNING' && progressData && (
                <div className="bg-gradient-to-br from-cyan-900/30 to-neutral-900 border border-cyan-500/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <ProgressRing progress={progressData.progress} size={48} strokeWidth={4} />
                      <div>
                        <div className="text-white font-semibold">Simulation in Progress</div>
                        <div className="text-cyan-400 text-sm">
                          Session {progressData.currentSession} of {progressData.totalSessions}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {progressData.elapsedSeconds !== null && (
                        <div className="text-neutral-400 text-sm">Elapsed: {formatDuration(progressData.elapsedSeconds)}</div>
                      )}
                      {progressData.estimatedRemainingSeconds !== null && progressData.estimatedRemainingSeconds > 0 && (
                        <div className={`text-lg font-semibold ${progressData.estimateReliable ? 'text-cyan-400' : 'text-yellow-400'}`}>
                          {!progressData.estimateReliable && '~'}{formatDuration(progressData.estimatedRemainingSeconds)} remaining
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Full progress bar */}
                  <div className="relative w-full h-3 bg-neutral-800 rounded-full overflow-hidden mb-2">
                    {progressData.totalSessions > 0 && (
                      <div
                        className="absolute left-0 top-0 h-full bg-yellow-500/20 border-r border-yellow-500/40"
                        style={{ width: `${Math.min(100, (5 / progressData.totalSessions) * 100)}%` }}
                      />
                    )}
                    <div
                      className={`h-full transition-all duration-300 relative z-10 ${
                        !progressData.estimateReliable ? 'bg-yellow-500' : 'bg-cyan-500'
                      }`}
                      style={{ width: `${progressData.progress}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="text-neutral-300">{progressData.progress}% complete</div>
                    {progressData.avgSecondsPerSession !== null && progressData.avgSecondsPerSession > 0 && (
                      <div className="text-neutral-500 text-xs">
                        Avg: {progressData.avgSecondsPerSession.toFixed(1)}s per session
                      </div>
                    )}
                  </div>

                  {progressData.currentDate && (
                    <div className="mt-2 text-xs text-cyan-400/80">
                      Processing: {progressData.currentDate}
                      {progressData.currentSessionTime && ` at ${progressData.currentSessionTime} ET`}
                    </div>
                  )}

                  {!progressData.estimateReliable && (
                    <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-500/20 rounded-lg text-xs text-yellow-400">
                      Time estimate will stabilize after warm-up ({Math.max(0, 5 - progressData.currentSession)} more sessions)
                    </div>
                  )}
                </div>
              )}

              {/* Metrics Summary */}
              {runMetrics && runMetrics.final !== null && (
                <div className="bg-gradient-to-br from-neutral-900 to-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-white font-semibold">Performance Metrics</h4>
                    <button
                      onClick={() => setShowStats(!showStats)}
                      className="text-neutral-400 hover:text-white transition-colors"
                      aria-label={showStats ? 'Hide metrics' : 'Show metrics'}
                    >
                      <svg className={`w-5 h-5 transition-transform ${showStats ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                  </div>
                  {showStats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <MetricCard
                        label="Initial Capital"
                        value={formatCurrency(runMetrics.initial)}
                        size="sm"
                      />
                      <MetricCard
                        label="Final Capital"
                        value={formatCurrency(runMetrics.final)}
                        {...(runMetrics.pnl !== null && { trend: runMetrics.pnl >= 0 ? 'up' : 'down' })}
                        size="sm"
                      />
                      <MetricCard
                        label="Return"
                        value={formatPercent(runMetrics.returnPct)}
                        {...(runMetrics.pnl !== null && { subValue: `${formatCurrency(runMetrics.pnl)} PnL` })}
                        {...(runMetrics.returnPct !== null && { trend: runMetrics.returnPct >= 0 ? 'up' : 'down' })}
                        size="sm"
                      />
                      <MetricCard
                        label="Win Rate"
                        value={runMetrics.winRate !== null ? `${runMetrics.winRate.toFixed(1)}%` : '--'}
                        subValue={`${runMetrics.winningTrades}W / ${runMetrics.losingTrades}L`}
                        size="sm"
                      />
                      <MetricCard
                        label="Total Trades"
                        value={runMetrics.totalTrades.toString()}
                        size="sm"
                      />
                      <MetricCard
                        label="Avg PnL"
                        value={runMetrics.avgPnl !== null ? formatCurrency(runMetrics.avgPnl) : '--'}
                        {...(runMetrics.avgPnl !== null && { trend: runMetrics.avgPnl >= 0 ? 'up' : 'down' })}
                        size="sm"
                      />
                      <MetricCard
                        label="Best Trade"
                        value={trades.length > 0 ? formatCurrency(Math.max(...trades.map(t => Number(t.pnl || 0)))) : '--'}
                        trend="up"
                        size="sm"
                      />
                      <MetricCard
                        label="Worst Trade"
                        value={trades.length > 0 ? formatCurrency(Math.min(...trades.map(t => Number(t.pnl || 0)))) : '--'}
                        trend="down"
                        size="sm"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Equity Chart */}
              {equityChartData.length > 1 && (
                <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">📈 Equity Curve</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={equityChartData}>
                        <defs>
                          <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="timestamp" stroke="#6b7280" tick={{ fontSize: 11 }} />
                        <YAxis
                          stroke="#6b7280"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                          domain={['dataMin - 500', 'dataMax + 500']}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                          }}
                          labelStyle={{ color: '#9ca3af' }}
                          formatter={(value: number) => [formatCurrency(value), 'Equity']}
                        />
                        {initialEquity && (
                          <ReferenceLine
                            y={initialEquity}
                            stroke="#6b7280"
                            strokeDasharray="3 3"
                            label={{ value: 'Initial', fill: '#6b7280', fontSize: 10 }}
                          />
                        )}
                        <Area
                          type="monotone"
                          dataKey="equity"
                          stroke="#22c55e"
                          strokeWidth={2}
                          fill="url(#equityGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Win/Loss + Trade Outcomes Charts */}
              {runMetrics && runMetrics.totalTrades > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Win/Loss Distribution */}
                  <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
                    <h4 className="text-white font-semibold mb-4">🥧 Win/Loss Distribution</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={winLossChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {winLossChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1f2937',
                              border: '1px solid #374151',
                              borderRadius: '8px',
                            }}
                            formatter={(value: number) => [value, 'Trades']}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            formatter={(value, entry: any) => (
                              <span style={{ color: '#e5e5e5' }}>
                                {value} ({entry.payload.value})
                              </span>
                            )}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center text-sm text-neutral-400 mt-2">
                      {runMetrics.winningTrades} wins | {runMetrics.losingTrades} losses
                    </div>
                  </div>

                  {/* Trade Outcomes */}
                  {tradeOutcomesData.length > 0 && (
                    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
                      <h4 className="text-white font-semibold mb-4">📊 Trade Outcomes</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={tradeOutcomesData}
                              cx="50%"
                              cy="50%"
                              outerRadius={90}
                              paddingAngle={2}
                              dataKey="value"
                            >
                              {tradeOutcomesData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1f2937',
                                border: '1px solid #374151',
                                borderRadius: '8px',
                              }}
                              formatter={(value: number) => [value, 'Trades']}
                            />
                            <Legend
                              verticalAlign="bottom"
                              height={36}
                              wrapperStyle={{ fontSize: '11px' }}
                              formatter={(value, entry: any) => (
                                <span style={{ color: '#e5e5e5' }}>
                                  {value} ({entry.payload.value})
                                </span>
                              )}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Monthly P&L Chart */}
              {monthlyPlData.length > 0 && (
                <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-4">📅 Monthly P&L</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyPlData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="month" stroke="#6b7280" tick={{ fontSize: 11 }} />
                        <YAxis
                          stroke="#6b7280"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => `$${v.toFixed(0)}`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                          }}
                          labelStyle={{ color: '#9ca3af' }}
                          formatter={(value: number) => [formatCurrency(value), 'P&L']}
                        />
                        <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                          {monthlyPlData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    AI Recommendations
                  </h4>
                  <span className="text-xs text-neutral-500 px-2 py-1 bg-neutral-800 rounded-full">Read-only</span>
                </div>
                {renderRecommendations()}
              </div>

              {/* Trades Table */}
              <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-neutral-800 flex items-center justify-between">
                  <h4 className="text-white font-semibold">Trades</h4>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-neutral-400">{trades.length} total trades</span>
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                      className="text-sm bg-neutral-800 border border-neutral-700 rounded px-2 py-1 text-white"
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                    </select>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-400 border-b border-neutral-800">
                        <th className="px-6 py-3 font-medium">Time</th>
                        <th className="px-6 py-3 font-medium">Symbol</th>
                        <th className="px-6 py-3 font-medium">Side</th>
                        <th className="px-6 py-3 font-medium text-right">Qty</th>
                        <th className="px-6 py-3 font-medium text-right">Price</th>
                        <th className="px-6 py-3 font-medium text-right">PnL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {paginatedTrades.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-8 text-center text-neutral-500">
                            No trades recorded yet
                          </td>
                        </tr>
                      ) : (
                        paginatedTrades.map((t) => {
                          const pnl = Number(t.pnl || 0);
                          return (
                            <tr key={t.id} className="hover:bg-neutral-800/30 transition-colors">
                              <td className="px-6 py-3 text-neutral-400">
                                {new Date(t.timestamp).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </td>
                              <td className="px-6 py-3 text-white font-medium">{t.symbol}</td>
                              <td className="px-6 py-3">
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    t.side === 'BUY'
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'bg-red-500/20 text-red-400'
                                  }`}
                                >
                                  {t.side}
                                </span>
                              </td>
                              <td className="px-6 py-3 text-neutral-200 text-right">{Number(t.quantity).toFixed(2)}</td>
                              <td className="px-6 py-3 text-neutral-200 text-right">${Number(t.price).toFixed(4)}</td>
                              <td className={`px-6 py-3 text-right font-medium ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {t.pnl !== null && t.pnl !== undefined ? `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}` : '--'}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalTradesPages > 1 && (
                  <div className="px-6 py-4 border-t border-neutral-800 flex items-center justify-between">
                    <div className="text-sm text-neutral-400">
                      Showing {tradesPage * TRADES_PER_PAGE + 1}-{Math.min((tradesPage + 1) * TRADES_PER_PAGE, trades.length)} of {trades.length}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setTradesPage((p) => Math.max(0, p - 1))}
                        disabled={tradesPage === 0}
                        className="px-3 py-1.5 text-sm bg-neutral-800 border border-neutral-700 rounded hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-neutral-400">
                        Page {tradesPage + 1} of {totalTradesPages}
                      </span>
                      <button
                        onClick={() => setTradesPage((p) => Math.min(totalTradesPages - 1, p + 1))}
                        disabled={tradesPage >= totalTradesPages - 1}
                        className="px-3 py-1.5 text-sm bg-neutral-800 border border-neutral-700 rounded hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimulationDashboard;
